import { type RefObject, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type StageNavItem = { id: string; label: string };

type Props = {
  stages: StageNavItem[];
  railRef: RefObject<HTMLDivElement>;
  onStageClick?: (stage: StageNavItem) => void;
};

// Sticky chip row that maps to the stage groups inside a horizontal kanban
// rail. Click a chip to smooth-scroll the rail to that stage; the active chip
// updates as the user scrolls so it doubles as a "you are here" map.
export const KanbanStageNavigator = ({ stages, railRef, onStageClick }: Props) => {
  const [activeId, setActiveId] = useState<string | undefined>(stages[0]?.id);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail || stages.length === 0) return;

    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const railRect = rail.getBoundingClientRect();
        // Active = the stage whose leading edge is closest to (but not past)
        // the rail's left edge plus a small offset that matches scroll-padding.
        const threshold = railRect.left + 16;
        let best: { id: string; distance: number } | undefined;
        for (const stage of stages) {
          const el = rail.querySelector(
            `[data-kanban-stage-id="${stage.id}"]`
          ) as HTMLElement | null;
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          const distance = rect.left - threshold;
          if (!best) {
            best = { id: stage.id, distance };
            continue;
          }
          const candidatePassed = distance <= 0;
          const bestPassed = best.distance <= 0;
          if (candidatePassed && bestPassed) {
            if (distance > best.distance) best = { id: stage.id, distance };
          } else if (candidatePassed && !bestPassed) {
            best = { id: stage.id, distance };
          } else if (!bestPassed && distance < best.distance) {
            best = { id: stage.id, distance };
          }
        }
        if (best) setActiveId(best.id);
      });
    };

    update();
    rail.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(rail);
    return () => {
      rail.removeEventListener("scroll", update);
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [stages, railRef]);

  const handleClick = (id: string) => {
    const rail = railRef.current;
    if (!rail) return;
    const el = rail.querySelector(
      `[data-kanban-stage-id="${id}"]`
    ) as HTMLElement | null;
    if (!el) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    rail.scrollTo({
      left: Math.max(0, el.offsetLeft - 12),
      behavior: reducedMotion ? "auto" : "smooth",
    });
    setActiveId(id);
    const stage = stages.find((s) => s.id === id);
    if (stage) onStageClick?.(stage);
  };

  if (stages.length <= 1) return null;

  return (
    <nav
      aria-label="Etapas del embudo"
      className="flex shrink-0 gap-2 overflow-x-auto border-b border-gray-200 bg-white px-3 py-2"
    >
      {stages.map((stage) => {
        const isActive = stage.id === activeId;
        return (
          <button
            key={stage.id}
            type="button"
            aria-current={isActive ? "true" : undefined}
            onClick={() => handleClick(stage.id)}
            className={cn(
              "min-h-11 shrink-0 rounded-full px-3.5 text-sm font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-palomma-primary focus-visible:ring-offset-2",
              isActive
                ? "bg-palomma-primary text-white"
                : "border border-gray-200 bg-white text-palomma-secondary hover:border-palomma-primary/40 hover:text-palomma-primary"
            )}
          >
            {stage.label}
          </button>
        );
      })}
    </nav>
  );
};
